import React from 'react';
import {
  Box,
  Paper,
  Skeleton,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';

/**
 * Skeleton loader for ticket table
 */
export const TicketTableSkeleton = ({ rows = 10 }) => (
  <TableContainer component={Paper}>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell><Skeleton variant="text" width={40} /></TableCell>
          <TableCell><Skeleton variant="text" width={200} /></TableCell>
          <TableCell><Skeleton variant="text" width={80} /></TableCell>
          <TableCell><Skeleton variant="text" width={80} /></TableCell>
          <TableCell><Skeleton variant="text" width={100} /></TableCell>
          <TableCell><Skeleton variant="text" width={120} /></TableCell>
          <TableCell><Skeleton variant="text" width={100} /></TableCell>
          <TableCell><Skeleton variant="text" width={100} /></TableCell>
          <TableCell><Skeleton variant="text" width={60} /></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {[...Array(rows)].map((_, rowIdx) => (
          <TableRow key={rowIdx}>
            <TableCell><Skeleton variant="text" width={30} /></TableCell>
            <TableCell><Skeleton variant="text" width="90%" /></TableCell>
            <TableCell><Skeleton variant="rounded" width={70} height={24} /></TableCell>
            <TableCell><Skeleton variant="rounded" width={70} height={24} /></TableCell>
            <TableCell><Skeleton variant="rounded" width={90} height={24} /></TableCell>
            <TableCell><Skeleton variant="text" width="80%" /></TableCell>
            <TableCell><Skeleton variant="text" width="70%" /></TableCell>
            <TableCell><Skeleton variant="text" width={80} /></TableCell>
            <TableCell><Skeleton variant="circular" width={32} height={32} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

/**
 * Skeleton loader for ticket cards (mobile view)
 */
export const TicketCardSkeleton = ({ count = 5 }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    {[...Array(count)].map((_, idx) => (
      <Paper key={idx} sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Skeleton variant="text" width={60} height={24} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Skeleton variant="rounded" width={60} height={24} />
            <Skeleton variant="rounded" width={60} height={24} />
          </Box>
        </Box>
        <Skeleton variant="text" width="80%" height={28} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="100%" />
        <Skeleton variant="text" width="60%" sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Skeleton variant="text" width={120} />
          <Skeleton variant="text" width={80} />
        </Box>
      </Paper>
    ))}
  </Box>
);

/**
 * Skeleton loader for ticket detail view
 */
export const TicketDetailSkeleton = () => (
  <Box>
    {/* Header */}
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" height={40} sx={{ mb: 1 }} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Skeleton variant="rounded" width={80} height={28} />
            <Skeleton variant="rounded" width={80} height={28} />
            <Skeleton variant="rounded" width={100} height={28} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="rounded" width={100} height={36} />
          <Skeleton variant="rounded" width={100} height={36} />
        </Box>
      </Box>
    </Paper>

    {/* Content Grid */}
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Skeleton variant="text" width="30%" height={28} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="80%" sx={{ mb: 3 }} />

          <Skeleton variant="text" width="30%" height={28} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="60%" />
        </Paper>

        {/* Tabs */}
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Skeleton variant="rounded" width={100} height={36} />
            <Skeleton variant="rounded" width={100} height={36} />
            <Skeleton variant="rounded" width={100} height={36} />
          </Box>
          <Skeleton variant="rectangular" width="100%" height={200} />
        </Paper>
      </Grid>

      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 3 }}>
          <Skeleton variant="text" width="50%" height={28} sx={{ mb: 2 }} />
          {[...Array(5)].map((_, idx) => (
            <Box key={idx} sx={{ mb: 2 }}>
              <Skeleton variant="text" width="40%" height={20} />
              <Skeleton variant="text" width="80%" height={24} />
            </Box>
          ))}
        </Paper>
      </Grid>
    </Grid>
  </Box>
);

export default {
  TicketTableSkeleton,
  TicketCardSkeleton,
  TicketDetailSkeleton
};
